import { AppDispatch } from '../definitions/types';
import { AppState } from '../redux/reducers/index';
import { generalRequestFail, loginLinkFailed, invalidUserIdToken } from '../redux/actions/errors';
import {
  ERR_INVALID_TOKEN,
  ERR_CANNOT_VERIFY_TOKEN,
  ERR_FAILED_TO_RETRIEVE_LOGIN_LINK,
} from './blobApi';
import {
  LANG_TO_LOCALE_MAP,
  DEFAULT_LANG,
  PACKAGE_TYPE_BUSINESS,
  PACKAGE_TYPE_PLATIN,
  PACKAGE_TYPE_SHOP,
  BUNDLE_TYPE_SHOP,
  PACKAGE_TYPE_SEO,
  BUNDLE_TYPE_SEO,
  BUNDLE_TYPE_PLATIN,
  BUNDLE_TYPE_PLATINUM,
  AFFILIATES_ALLOWED_FOR_DOLPHIN_WHITELIST,
  BUNDLE_TYPE_SEO_JAPAN,
} from './constants';
import reload from './reload';
import {
  PackageType,
  ValidCheckoutPackageType,
  ValidCheckoutBundleType,
} from '../redux/reducers/pricing';
import { RichTracking, getRichTrackingEndpoint } from '@jimdo/frontend-tracking';
import { getUserAccountIdFromCookie, getSHD } from '../modules/cookies';
import affiliate from '../modules/affiliate';

export const getWindow = (): Window => {
  return typeof window !== 'undefined' ? window : undefined; // tslint:disable-line no-typeof-undefined
};

export const getDocument = (): Document => {
  return typeof document !== 'undefined' ? document : undefined; // tslint:disable-line no-typeof-undefined
};

export const getLocation = (): Location => {
  const win = getWindow();
  if (!win) {
    return undefined;
  }
  return win.location;
};

export const getJimDom = (): {}[] => {
  const win = getWindow();

  if (!win) {
    return undefined;
  }

  win.__jimDom = win.__jimDom || [];
  return win.__jimDom;
};

export const getBunchBox = (): {}[] => {
  const win = getWindow();

  if (!win) {
    return undefined;
  }

  win._bb = win._bb || [];
  return win._bb;
};

export const getJimDoge = (): {}[] => {
  const win = getWindow();

  if (!win) {
    return undefined;
  }

  win._jimDoge = win._jimDoge || [];
  return win._jimDoge;
};

export const getGtmDataLayer = () => {
  const win = getWindow();

  if (win === undefined) {
    return [];
  }

  win.dataLayer = win.dataLayer || [];
  return win.dataLayer;
};

export const getDwh = () => {
  return RichTracking({
    endpoint: getRichTrackingEndpoint(getWindow().location.hostname),
    env: 'signup',
    anonId: () => getSHD(),
    userAccountId: () => getUserAccountIdFromCookie(),
    websiteId: () => '',
    isAffiliate: () => {
      // affiliate() when called like this just reads the affiliate cookie
      // if it returns something other than undefined, we should return true here
      return !!affiliate({}, '');
    },
    logToConsole: false,
  });
};

export function langToLocale(lang: string = DEFAULT_LANG): string {
  const locale = LANG_TO_LOCALE_MAP[lang];
  if (!locale) {
    return LANG_TO_LOCALE_MAP[DEFAULT_LANG];
  }
  return locale;
}

export function isBase64(str: string): boolean {
  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
}

export interface Errors {
  [x: string]: string;
}

export const handleError = (dispatch: AppDispatch, errors: Errors): void => {
  const firstError = Object.keys(errors)
    .slice(0, 1)
    .map((k: string) => errors[k])
    .shift();

  switch (firstError) {
    case ERR_INVALID_TOKEN:
    case ERR_CANNOT_VERIFY_TOKEN:
      dispatch(invalidUserIdToken());
      setTimeout(reload);
      break;
    case ERR_FAILED_TO_RETRIEVE_LOGIN_LINK:
      dispatch(loginLinkFailed());
      break;
    default:
      dispatch(generalRequestFail());
  }
};

export const isAustraliaCountry = (country: string): boolean => {
  return country === 'AU';
};

export const isGermanyCountry = (country: string): boolean => {
  return country === 'DE';
};

export const isSwitzerLandCountry = (country: string): boolean => {
  return country === 'CH';
};

export const isAustriaCountry = (country: string): boolean => {
  return country === 'AT';
};

export const isJapanCountry = (country: string): boolean => {
  return country === 'JP';
};

export const isGermanLanguage = (language: string): boolean => {
  return language === 'de';
};

export const isEnglishLanguage = (language: string): boolean => {
  return language === 'en';
};

export const isFrenchLanguage = (language: string): boolean => {
  return language === 'fr';
};

export const isDutchLanguage = (language: string): boolean => {
  return language === 'nl';
};

export const isJapaneseLanguage = (language: string): boolean => {
  return language === 'ja';
};

export const isSpanishLanguage = (language: string): boolean => {
  return language === 'es';
};

export const isItalianLanguage = (language: string): boolean => {
  return language === 'it';
};

export const mapToValidCheckoutPackageType = (
  packageType: PackageType
): ValidCheckoutPackageType => {
  switch (packageType) {
    case PACKAGE_TYPE_SHOP:
    case PACKAGE_TYPE_SEO:
    case PACKAGE_TYPE_PLATIN:
      return PACKAGE_TYPE_BUSINESS;
    default:
      return packageType as ValidCheckoutPackageType;
  }
};

export const mapToValidCheckoutBundleType = (
  packageType: PackageType,
  lang: string
): ValidCheckoutBundleType => {
  switch (packageType) {
    case PACKAGE_TYPE_SHOP:
      return BUNDLE_TYPE_SHOP as ValidCheckoutBundleType;
    case PACKAGE_TYPE_SEO:
      return isJapaneseLanguage(lang)
        ? (BUNDLE_TYPE_SEO_JAPAN as ValidCheckoutBundleType)
        : (BUNDLE_TYPE_SEO as ValidCheckoutBundleType);
    case PACKAGE_TYPE_PLATIN:
      return isGermanLanguage(lang)
        ? (BUNDLE_TYPE_PLATIN as ValidCheckoutBundleType)
        : (BUNDLE_TYPE_PLATINUM as ValidCheckoutBundleType);
    default:
      return null;
  }
};

export const isUserAllowedToEnterDolphin = (
  affiliateId: string,
  skipTemplateView: boolean,
  hasValidTrial: boolean,
  shouldSkipProductChoiceView: boolean = false,
  excludeFromShop: boolean = false
): boolean => {
  const isAllowedAffiliate =
    affiliateId === '' || AFFILIATES_ALLOWED_FOR_DOLPHIN_WHITELIST.includes(affiliateId);

  return (
    !skipTemplateView &&
    isAllowedAffiliate &&
    !hasValidTrial &&
    !shouldSkipProductChoiceView &&
    !excludeFromShop
  );
};

export const isLoggedIn = async (): Promise<boolean> => {
  try {
    const response = await fetch('/api/loggedIn', {
      credentials: 'same-origin',
    });
    return await response.json();
  } catch {
    return false;
  }
};

const getSimplifiedAppState = (appState: AppState) => {
  return {
    intent: {
      industry: appState.intent.industry,
      other: appState.intent.other,
      selectedProduct: appState.intent.selectedProduct,
    },
    pricing: {
      selectedPackage: appState.pricing.selectedPackage,
      skipPricingView: appState.pricing.skipPricingView,
    },
    domain: appState.domain,
    template: appState.template,
    user: {
      idToken: appState.user.idToken,
    },
  };
};

export const redirectToLogin = (path: String, appState: AppState): void => {
  const baseUrl = /(http(s)?:\/\/register\.jimdo.*\.[a-z]*\/[a-z]{2}).*/.exec(
    window.location.href
  )[1];
  const redirect = encodeURIComponent(`${baseUrl}${path}${window.location.search}`);
  const state = btoa(JSON.stringify(getSimplifiedAppState(appState)));
  window.location.href = `/login?redirect=${redirect}&state=${state}`;
};

const rankingCoachCountriesUrl = 'https://a.jimdo.com/app/price/index/rankingcoachcountries';
const userCountryUrl = 'https://a.jimdo.com/app/price/index/country';

export const canHaveRankingCoach = async (): Promise<boolean> => {
  const rankingCoachCountriesResponse = await fetch(rankingCoachCountriesUrl);
  const rankingCoachCountries = await rankingCoachCountriesResponse.json();
  const userCountryResponse = await fetch(userCountryUrl);
  const userCountry = await userCountryResponse.json();

  if (rankingCoachCountries.includes(userCountry.country)) {
    return true;
  } else {
    return false;
  }
};
